import { Descendant } from 'slate';

import { FormulaType } from '../../../constants/formulaType';
// constants
import { INSUFFICIENT_ALTERNATES_COLUMNS } from './constants';

//helpers
import { getEmailFieldData } from '../../../helpers/formulaHelpers';
import { sanitizeForExcel } from '../../../helpers/sanitize.helper';

import { ClipboardTransformedData } from '../../../models/alternate.model';
import { ComponentTypeMap } from '../../../models/component-types-model';
//models
import { ElementKind, SectionType } from '../../../models/editor.model';
import {
    AlternateHeaderChildrenType,
    AlternateHeaderType,
} from '../../../models/formula.model';
import { VariantType, VariantWithLabel } from '../../../models/variant.model';

// services
import { toastService } from '../../../services/toast.service';

const elementKindText = (elementKind: ElementKind) => {
    switch (elementKind) {
        case ElementKind.link:
            return 'Link';
        default:
            return 'Text';
    }
};

export const sectionHeading = (
    sectionType: SectionType,
    formulaType: FormulaType,
    surfaceFieldLabel?: string,
) => {
    if (surfaceFieldLabel) {
        return surfaceFieldLabel;
    }
    if (formulaType === FormulaType.email) {
        return getEmailFieldData(sectionType).label;
    }
    if (formulaType === FormulaType.contentCards) {
        const headline = (sectionType?.replaceAll('_', ' ') || '')?.replace(
            /\btext\b/gi,
            '',
        );
        return headline?.charAt(0)?.toUpperCase() + headline?.slice(1);
    }
    return sectionType;
};

const transformChild = (
    child: any,
    componentTypes: ComponentTypeMap,
): AlternateHeaderChildrenType[] => {
    const children = [];
    if (!child.kind && !child.text.trim()) {
        return [];
    }
    if (child.kind === ElementKind.section) {
        for (const childSection of child.children) {
            children.push(...transformChild(childSection, componentTypes));
        }
    } else if (child.kind === 'variant') {
        const variantType = child.variantType;
        const variantTypeData = componentTypes?.[variantType as string];
        children.push({
            headerName: `${variantTypeData?.name}`,
            variantType: variantType,
            id: child.id,
            kind: child.kind,
        });
    } else if (child?.kind === ElementKind.dataset) {
        children.push({
            headerName: 'Dataset',
            id: child.id,
            kind: child.kind,
            datasetId: child.datasetId,
            datasetField: child.datasetField,
        });
    } else if (child?.kind === ElementKind.cms_placeholder) {
        children.push({
            headerName: 'Cms',
            id: child.cmsPlaceholderId,
            kind: child.kind,
        });
    } else if (child?.kind === ElementKind.attributePlaceholder) {
        children.push({
            headerName: 'Attribute',
            id: child.id,
            defaultValue: child.defaultValue,
            kind: child.kind,
            attributeName: child.attributeName,
        });
    } else {
        children.push({
            ...child,
            headerName: elementKindText(child?.kind),
            text: child.text,
            id: child.id,
            kind: child.kind,
        });
    }
    return children;
};

export const transformHeaderData = (
    formulaStructure: Descendant[],
    formulaType: FormulaType,
    componentTypes: ComponentTypeMap,
) => {
    const resultedData: AlternateHeaderType[] = [];
    const sectionOrder: string[] = [];
    formulaStructure.forEach((section: any) => {
        const sectionType = sectionHeading(
            section.sectionType,
            formulaType,
            section?.surfaceFieldLabel,
        );
        const children = section.children;
        if (
            section.variantType === VariantType.Image ||
            (!sectionType && section.kind === ElementKind.image)
        ) {
            return null;
        }
        const mainHeader: AlternateHeaderType = {
            headerName: sectionType,
            children: [],
        };
        children.forEach((child: any) => {
            mainHeader.children.push(...transformChild(child, componentTypes));
        });
        if (mainHeader.children.length > 0) {
            resultedData.push(mainHeader);
            sectionOrder.push(sectionType);
        }
    });
    return {
        headers: resultedData,
        sectionOrder: sectionOrder.map(
            (item) => item.charAt(0).toUpperCase() + item.slice(1),
        ),
    };
};

export const transformClipboardData = (
    data: string[][],
): ClipboardTransformedData | undefined => {
    // Parse section boundaries from the first row (section names at non-empty cells)
    const sectionBoundaries: Array<{ name: string; startIdx: number }> = [];
    data[0].forEach((cell, idx) => {
        if (cell.trim()) {
            sectionBoundaries.push({ name: cell.trim(), startIdx: idx });
        }
    });

    if (sectionBoundaries.length === 0) return undefined;

    const rowWidth = data[0].length;

    const AlternatesColumnCount = data[2].filter(
        (column: string) => column === 'Alternate',
    ).length;
    const totalSubHeaders = data[1].filter(
        (subHeader: string) => !!subHeader && subHeader !== 'Text',
    ).length;

    if (AlternatesColumnCount < totalSubHeaders) {
        toastService.showError(INSUFFICIENT_ALTERNATES_COLUMNS);
        return;
    }

    // Helper function to process sections
    const processSection = (subHeaders: string[], sectionData: string[][]) => {
        const result = [];
        for (let i = 0; i < subHeaders.length; i += 2) {
            const type = subHeaders[i];
            if (type) {
                const alternates = [];
                const encounteredAlternates = new Set<string>();
                for (let j = 0; j < sectionData.length; j++) {
                    const alternate = sectionData[j][i];
                    const label = sectionData[j][i + 1];
                    if (
                        alternate &&
                        label !== undefined &&
                        !encounteredAlternates.has(alternate)
                    ) {
                        encounteredAlternates.add(alternate);
                        alternates.push({ alternate, label });
                    }
                }
                result.push({
                    type,
                    alternate_labels: alternates,
                });
            }
        }
        return result;
    };

    const result: ClipboardTransformedData = {};

    sectionBoundaries.forEach(({ name, startIdx }, i) => {
        const endIdx =
            i < sectionBoundaries.length - 1
                ? sectionBoundaries[i + 1].startIdx
                : rowWidth;

        const rawSubHeaders = data[1].slice(startIdx, endIdx);
        const textIndices = rawSubHeaders.reduce<number[]>(
            (indices, header, index) => {
                if (header === 'Text') {
                    indices.push(index);
                }
                return indices;
            },
            [],
        );
        const subHeaders = rawSubHeaders.filter((sh) => sh !== 'Text');
        const sectionData = data
            .slice(3)
            .map((row) =>
                row
                    .slice(startIdx, endIdx)
                    .filter((_, index) => !textIndices.includes(index)),
            );

        const components = processSection(subHeaders, sectionData);
        if (components.length > 0) {
            result[name] = components;
        }
    });

    return Object.keys(result).length > 0 ? result : undefined;
};

export const getSectionVariantType = (
    headerData: AlternateHeaderType[],
    componentTypes: ComponentTypeMap,
) => {
    const variantMap: Record<
        string,
        { section: string; variantType?: string }
    > = {};

    headerData?.forEach(({ headerName, children }) => {
        const sectionName = headerName
            ? headerName.charAt(0).toUpperCase() + headerName.slice(1)
            : '';

        const traverse = (nodes: AlternateHeaderChildrenType[]) => {
            for (const node of nodes) {
                if (node.id) {
                    variantMap[node.id] = {
                        section: sectionName,
                        variantType: `${
                            componentTypes?.[node.variantType as string]?.name
                        }_${node.id}`,
                    };
                }
                if (node.children?.length) traverse(node.children);
            }
        };

        traverse(children || []);
    });

    return variantMap;
};

export const groupVariantAlternatesWithLabels = (
    alternates: VariantWithLabel[],
    variantMap: ReturnType<typeof getSectionVariantType>,
) => {
    const grouped: Record<
        string,
        { type: string; rows: { alternate: string; label: string }[] }[]
    > = {};

    if (!alternates) return grouped;

    const sortedAlternates = [...alternates].sort((a, b) => {
        const isMasterA = a.id === a.component_id;
        const isMasterB = b.id === b.component_id;
        if (isMasterA) return -1;
        if (isMasterB) return 1;
        return (a.label ? 1 : 0) - (b.label ? 1 : 0);
    });

    sortedAlternates.forEach((item) => {
        const meta = variantMap[item.component_id];
        if (!meta) return;

        const { section, variantType = '' } = meta;
        if (!grouped[section]) grouped[section] = [];

        let typeBlock = grouped[section].find(
            (block) => block.type === variantType,
        );
        if (!typeBlock) {
            typeBlock = { type: variantType, rows: [] };
            grouped[section].push(typeBlock);
        }

        typeBlock.rows.push({
            alternate: item.content || '',
            label: item?.label?.label_name || '',
        });
    });

    return grouped;
};

const escapeCsvValue = (value: string): string => {
    // Sanitize to prevent Excel formula injection
    const sanitized = sanitizeForExcel(value);
    // Handle CSV-special characters
    if (
        sanitized.includes(',') ||
        sanitized.includes('"') ||
        sanitized.includes('\n')
    ) {
        return `"${sanitized.replace(/"/g, '""')}"`;
    }
    return sanitized;
};

export const generateCsvContent = (
    grouped: ReturnType<typeof groupVariantAlternatesWithLabels>,
    sectionOrder: string[],
    sectionTypeOrderMap: Record<string, string[]>,
) => {
    const csvRows: string[][] = [];

    const allSections = (sectionOrder || Object.keys(grouped)).filter(
        (section) => {
            const blocks = grouped[section];
            return blocks?.some((block) => block.rows.length > 0);
        },
    );

    const sectionTypes: Record<string, string[]> = {};
    let maxRows = 0;

    allSections.forEach((section) => {
        const typesInOrder = sectionTypeOrderMap[section] || [];

        grouped[section]?.sort((a, b) => {
            const aIndex = typesInOrder.indexOf(a.type);
            const bIndex = typesInOrder.indexOf(b.type);
            return (
                (aIndex === -1 ? Infinity : aIndex) -
                (bIndex === -1 ? Infinity : bIndex)
            );
        });

        sectionTypes[section] =
            grouped[section]
                ?.filter((g) => g.rows.length > 0)
                .map((g) => g.type) || [];

        grouped[section]?.forEach(({ rows }) => {
            maxRows = Math.max(maxRows, rows.length);
        });
    });

    if (allSections.length === 0) return '';

    csvRows.push(
        allSections.flatMap((section) => [
            section,
            ...Array(
                sectionTypes[section].length
                    ? sectionTypes[section].length * 2 - 1
                    : 1,
            ).fill(''),
        ]),
    );

    // Variant Type Headers
    csvRows.push(
        allSections.flatMap((section) =>
            sectionTypes[section].flatMap((type) => [type.split('_')[0], '']),
        ),
    );

    // "Alternate" & "Label" headers
    csvRows.push(
        allSections.flatMap((section) =>
            sectionTypes[section].flatMap(() => ['Alternate', 'Label']),
        ),
    );

    // Row data
    for (let i = 0; i < maxRows; i++) {
        csvRows.push(
            allSections.flatMap((section) =>
                sectionTypes[section].flatMap((type) => {
                    const typeBlock = grouped[section].find(
                        (g) => g.type === type,
                    );

                    const entry = typeBlock?.rows[i];
                    return [
                        escapeCsvValue(entry?.alternate || ''),
                        escapeCsvValue(entry?.label || ''),
                    ];
                }),
            ),
        );
    }

    return csvRows.map((r) => r.join(',')).join('\n');
};
